import type { APIKeyConfig } from "@/types/api-key";

const DEFAULT_BASE = "https://api.cometapi.com";

function isGeminiModel(model: string): boolean {
  return /gemini/i.test(model);
}

function isFluxModel(model: string): boolean {
  const lower = model.toLowerCase();
  return lower.includes("flux-2-") || lower.includes("flux/") || lower.startsWith("black-forest-labs/flux");
}

export async function validateCometAPIKey(config: {
  apiKey: string;
  model: string;
  customEndpoint?: string;
}): Promise<{ isValid: boolean; error?: string }> {
  if (!config.apiKey || !config.model) {
    return { isValid: false, error: "API key and model are required" };
  }
  const base = config.customEndpoint && config.customEndpoint.trim().length > 0
    ? config.customEndpoint.replace(/\/$/, "")
    : DEFAULT_BASE;
  const preferredUrl = /\/v1\//.test(base) ? base.replace(/\/$/, "") : `${base}/v1`;
  const url = `${preferredUrl}/models`;
  try {
    const resp = await fetch(url, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${config.apiKey}`,
      },
    });
    if (!resp.ok) {
      let msg = `HTTP ${resp.status}`;
      try {
        const err = await resp.json();
        msg = String(err?.error?.message || err?.message || msg);
      } catch {}
      return { isValid: false, error: msg };
    }
    return { isValid: true };
  } catch (e: any) {
    return { isValid: false, error: String(e?.message || e) };
  }
}

function buildCometEndpoint(base: string | undefined | null, path: 'generations' | 'edits'): string {
  const trimmed = (base && base.trim().length > 0 ? base.trim() : DEFAULT_BASE).replace(/\/$/, '');

  if (/\/v1\/images\/(generations|edits)$/i.test(trimmed)) {
    return trimmed.replace(/\/(generations|edits)$/i, `/${path}`);
  }
  if (/\/v1\/images$/i.test(trimmed)) {
    return `${trimmed}/${path}`;
  }
  if (/\/v1$/i.test(trimmed)) {
    return `${trimmed}/images/${path}`;
  }
  return `${trimmed}/v1/images/${path}`;
}

function buildGeminiEndpoint(base: string | undefined | null, model: string): string {
  const trimmed = (base && base.trim().length > 0 ? base.trim() : DEFAULT_BASE).replace(/\/$/, '');
  const modelName = model.includes('-preview') ? model : `${model}-preview`;
  return `${trimmed}/v1beta/models/${modelName}:generateContent`;
}

async function generateImageCometAPIGemini(
  prompt: string,
  api: Pick<APIKeyConfig, "apiKey" | "model" | "temperature" | "customEndpoint">,
  opts?: { signal?: AbortSignal; referenceImageData?: string; referenceImageUrl?: string; size?: string }
): Promise<{ imageData: string }> {
  const url = buildGeminiEndpoint(api.customEndpoint, api.model);
  const headers: Record<string, string> = {
    Authorization: api.apiKey,
    "Content-Type": "application/json",
  };

  const hasReferenceData = Boolean(opts?.referenceImageData && opts.referenceImageData.trim().length > 0);
  const hasReferenceUrl = Boolean(opts?.referenceImageUrl && opts.referenceImageUrl.trim().length > 0);
  const hasReference = hasReferenceData || hasReferenceUrl;

  const parts: Array<{ text?: string; inline_data?: { mime_type: string; data: string } }> = [{ text: prompt }];

  if (hasReference) {
    let referenceBase64: string;
    let mimeType = "image/jpeg";

    if (hasReferenceData) {
      const trimmed = opts!.referenceImageData!.trim();
      const dataUriMatch = trimmed.match(/^data:([^;]+);base64,(.+)$/);
      if (dataUriMatch) {
        mimeType = dataUriMatch[1] || "image/png";
        referenceBase64 = dataUriMatch[2];
      } else {
        referenceBase64 = trimmed.replace(/\s+/g, "");
      }
    } else {
      const fetched = await fetch(opts!.referenceImageUrl!.trim(), { signal: opts?.signal });
      if (!fetched.ok) {
        throw new Error(`Failed to download reference image: HTTP ${fetched.status}`);
      }
      const blob = await fetched.blob();
      mimeType = blob.type || "image/png";
      const arrayBuffer = await blob.arrayBuffer();
      const bytes = new Uint8Array(arrayBuffer);
      referenceBase64 = btoa(String.fromCharCode(...bytes));
    }

    parts.push({
      inline_data: {
        mime_type: mimeType,
        data: referenceBase64,
      },
    });
  }

  const body: any = {
    contents: [
      {
        role: "user",
        parts,
      },
    ],
  };

  if (hasReference) {
    body.generationConfig = {
      responseModalities: ["TEXT", "IMAGE"],
    };
  }

  const resp = await fetch(url, {
    method: "POST",
    headers,
    body: JSON.stringify(body),
    signal: opts?.signal,
  });

  if (!resp.ok) {
    let msg = `HTTP ${resp.status}`;
    try {
      const err = await resp.json();
      msg = err?.error?.message || err?.message || msg;
    } catch {}
    throw new Error(msg);
  }

  return await parseGeminiImageResponse(resp);
}

async function generateImageCometAPIFlux(
  prompt: string,
  api: Pick<APIKeyConfig, "apiKey" | "model" | "temperature" | "customEndpoint">,
  opts?: { signal?: AbortSignal; referenceImageData?: string; referenceImageUrl?: string; size?: string }
): Promise<{ imageData: string }> {
  const base = api.customEndpoint && api.customEndpoint.trim().length > 0
    ? api.customEndpoint.replace(/\/$/, "")
    : DEFAULT_BASE;

  // Extract model variant (pro, dev, flex)
  const lowerModel = api.model.toLowerCase();
  let modelVariant = "flux-2-pro";
  if (lowerModel.includes("flux-2-dev")) {
    modelVariant = "flux-2-dev";
  } else if (lowerModel.includes("flux-2-flex")) {
    modelVariant = "flux-2-flex";
  }

  // Build request payload
  const payload: Record<string, unknown> = {
    model: modelVariant,
    prompt,
    seed: -1,
  };

  // Handle reference image for image-to-image (edit mode)
  const hasReferenceData = Boolean(opts?.referenceImageData && opts.referenceImageData.trim().length > 0);
  const hasReferenceUrl = Boolean(opts?.referenceImageUrl && opts.referenceImageUrl.trim().length > 0);

  if (hasReferenceUrl) {
    payload.images = [opts!.referenceImageUrl!.trim()];
  } else if (hasReferenceData) {
    // Flux API requires URLs. Check if it's already a URL
    const trimmed = opts!.referenceImageData!.trim();
    if (/^https?:\/\//i.test(trimmed)) {
      payload.images = [trimmed];
    } else {
      console.warn("CometAPI Flux requires image URLs, not base64 data. Proceeding without reference image.");
    }
  }

  const headers: Record<string, string> = {
    Authorization: `Bearer ${api.apiKey}`,
    "Content-Type": "application/json",
  };

  // Submit generation request
  const submitUrl = `${base}/flux/generate_image`;
  const resp = await fetch(submitUrl, {
    method: "POST",
    headers,
    body: JSON.stringify(payload),
    signal: opts?.signal,
  });

  if (!resp.ok) {
    let msg = `HTTP ${resp.status}`;
    try {
      const err = await resp.json();
      msg = err?.error?.message || err?.message || msg;
    } catch {}
    throw new Error(msg);
  }

  const data = await resp.json();

  // Check if image is returned directly
  const directImage = extractFluxImageData(data);
  if (directImage) {
    return { imageData: directImage };
  }

  // Extract request_id for polling
  const requestId = data?.request_id || data?.requestId || data?.id || data?.task_id;
  if (!requestId) {
    throw new Error("CometAPI Flux did not return request_id or image");
  }

  // Poll for result
  const resultUrl = `${base}/image/flux/get_result`;
  const startTime = Date.now();
  const timeoutMs = 90000;
  let delay = 1000;

  while (Date.now() - startTime < timeoutMs) {
    await new Promise((r) => setTimeout(r, delay));
    delay = Math.min(5000, Math.floor(delay * 1.5));

    const pollResp = await fetch(`${resultUrl}?request_id=${encodeURIComponent(requestId)}`, {
      method: "GET",
      headers: { Authorization: `Bearer ${api.apiKey}` },
      signal: opts?.signal,
    });

    if (pollResp.status === 202 || pollResp.status === 204) {
      continue; // Still processing
    }

    if (!pollResp.ok) {
      let msg = `HTTP ${pollResp.status}`;
      try {
        const err = await pollResp.json();
        msg = err?.error?.message || err?.message || msg;
      } catch {}
      throw new Error(msg);
    }

    const pollData = await pollResp.json();
    const status = (pollData?.status || "").toLowerCase();

    if (status === "failed" || status === "error") {
      const msg = pollData?.error?.message || pollData?.message || "Flux generation failed";
      throw new Error(msg);
    }

    const imageData = extractFluxImageData(pollData);
    if (imageData) {
      return { imageData };
    }

    if (status === "completed" || status === "succeeded" || status === "success") {
      throw new Error("Flux completed but no image data found");
    }
  }

  throw new Error("CometAPI Flux did not complete in time");
}

function extractFluxImageData(payload: any): string | null {
  if (!payload || typeof payload !== "object") return null;

  // Check for direct image URL or base64
  for (const key of ["image", "image_url", "imageUrl", "url", "output", "result"]) {
    const value = payload[key];
    if (typeof value === "string" && value.trim()) {
      const trimmed = value.trim();
      if (trimmed.startsWith("data:image/")) {
        return trimmed;
      }
      // Return URL directly - caller will need to fetch if needed
      if (/^https?:\/\//i.test(trimmed)) {
        return trimmed;
      }
    }
  }

  // Check for outputs array
  const outputs = payload.outputs || payload.images || payload.data;
  if (Array.isArray(outputs)) {
    for (const item of outputs) {
      if (typeof item === "string") {
        const trimmed = item.trim();
        if (trimmed.startsWith("data:image/") || /^https?:\/\//i.test(trimmed)) {
          return trimmed;
        }
      } else if (item && typeof item === "object") {
        const nested = extractFluxImageData(item);
        if (nested) return nested;
      }
    }
  }

  // Check nested data object
  if (payload.data && typeof payload.data === "object" && !Array.isArray(payload.data)) {
    return extractFluxImageData(payload.data);
  }

  return null;
}

export async function generateImageCometAPI(
  prompt: string,
  api: Pick<APIKeyConfig, "apiKey" | "model" | "temperature" | "customEndpoint">,
  opts?: { signal?: AbortSignal; referenceImageData?: string; referenceImageUrl?: string; size?: string }
): Promise<{ imageData: string }> {
  if (isGeminiModel(api.model)) {
    return generateImageCometAPIGemini(prompt, api, opts);
  }

  if (isFluxModel(api.model)) {
    return generateImageCometAPIFlux(prompt, api, opts);
  }

  const url = buildCometEndpoint(api.customEndpoint, 'generations');
  const editUrl = buildCometEndpoint(api.customEndpoint, 'edits');
  const headers: Record<string, string> = {
    Authorization: `Bearer ${api.apiKey}`,
  };
  const size = (opts?.size && opts.size.trim()) || "4K";
  const hasReferenceData = Boolean(opts?.referenceImageData && opts.referenceImageData.trim().length > 0);
  const hasReferenceUrl = Boolean(opts?.referenceImageUrl && opts.referenceImageUrl.trim().length > 0);
  const hasReference = hasReferenceData || hasReferenceUrl;

  let resp: Response;
  if (hasReference) {
    const form = new FormData();
    form.append("model", api.model);
    form.append("prompt", prompt);
    form.append("size", size);
    form.append("response_format", "b64_json");
    if (typeof api.temperature === "number" && Number.isFinite(api.temperature)) {
      form.append("temperature", String(api.temperature));
    }
    if (hasReferenceData) {
      const { blob, filename } = createBlobFromReference(opts!.referenceImageData!.trim());
      form.append("image", blob, filename);
    } else if (hasReferenceUrl) {
      const fetched = await fetch(opts!.referenceImageUrl!.trim(), { signal: opts?.signal });
      if (!fetched.ok) {
        throw new Error(`Failed to download reference image: HTTP ${fetched.status}`);
      }
      const blob = await fetched.blob();
      const filename = inferFilenameFromUrl(opts!.referenceImageUrl!.trim(), blob.type || 'image/png');
      form.append("image", blob, filename);
    }
    const endpoint = editUrl;
    resp = await fetch(endpoint, {
      method: "POST",
      headers,
      body: form,
      signal: opts?.signal,
    });
  } else {
    const body: Record<string, unknown> = {
      model: api.model,
      prompt,
      size,
      response_format: "b64_json",
    };
    if (typeof api.temperature === "number" && Number.isFinite(api.temperature)) {
      body.temperature = api.temperature;
    }
    resp = await fetch(url, {
      method: "POST",
      headers: {
        ...headers,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
      signal: opts?.signal,
    });
  }

  if (!resp.ok) {
    let msg = `HTTP ${resp.status}`;
    try {
      const err = await resp.json();
      msg = err?.error?.message || err?.message || msg;
    } catch {}
    throw new Error(msg);
  }

  return await parseCometImageResponse(resp, opts?.signal);
}

async function parseGeminiImageResponse(resp: Response): Promise<{ imageData: string }> {
  const data = await resp.json();

  const candidates = data?.candidates;
  if (!Array.isArray(candidates) || candidates.length === 0) {
    throw new Error("No image data returned by CometAPI Gemini response");
  }

  const firstCandidate = candidates[0];
  const content = firstCandidate?.content;
  const parts = content?.parts;

  if (!Array.isArray(parts)) {
    throw new Error("No image data returned by CometAPI Gemini response");
  }

  for (const part of parts) {
    const inlineData = part?.inline_data || part?.inlineData;
    if (inlineData?.data) {
      const base64Data = String(inlineData.data).trim();
      const mimeType = inlineData.mime_type || inlineData.mimeType || "image/png";
      if (base64Data.startsWith("data:image/")) {
        return { imageData: base64Data };
      }
      return { imageData: `data:${mimeType};base64,${base64Data}` };
    }
  }

  throw new Error("No image data returned by CometAPI Gemini response");
}

async function parseCometImageResponse(resp: Response, signal?: AbortSignal): Promise<{ imageData: string }>{
  const data = await resp.json();
  const directUrl: string | undefined = data?.data?.[0]?.url || data?.url || data?.image_url;
  if (typeof directUrl === "string" && directUrl.startsWith("http")) {
    try {
      const imgResp = await fetch(directUrl, { signal });
      if (!imgResp.ok) throw new Error(`HTTP ${imgResp.status}`);
      const blob = await imgResp.blob();
      const dataUri = await blobToDataURL(blob);
      if (dataUri && /^data:image\//i.test(dataUri)) {
        return { imageData: dataUri };
      }
    } catch {
      return { imageData: directUrl };
    }
  }
  let b64: string | undefined = data?.data?.[0]?.b64_json || data?.b64_json || data?.image_base64 || data?.image;
  if (typeof b64 === "string" && b64.length > 100 && !/^data:image\//i.test(b64)) {
    return { imageData: `data:image/png;base64,${b64}` };
  }
  if (typeof b64 === "string" && /^data:image\//i.test(b64)) {
    return { imageData: b64 };
  }
  const contentStr = String(data?.choices?.[0]?.message?.content || data?.content || "").trim();
  const uriMatch = contentStr.match(/data:image\/(png|jpe?g|webp);base64,[A-Za-z0-9+/=]+/i);
  if (uriMatch) return { imageData: uriMatch[0] };
  const whole = JSON.stringify(data);
  const anyUri = whole.match(/data:image\/(png|jpe?g|webp);base64,[A-Za-z0-9+/=]+/i);
  if (anyUri) return { imageData: anyUri[0] };
  const mB64 = whole.match(/"(b64_json|image_base64|image)"\s*:\s*"([A-Za-z0-9+/=]{100,})"/);
  if (mB64) return { imageData: `data:image/png;base64,${mB64[2]}` };
  throw new Error("No image data returned by CometAPI response");
}

function inferFilenameFromUrl(url: string, mime: string): string {
  try {
    const parsed = new URL(url);
    const base = parsed.pathname.split('/').pop() || 'reference';
    if (/\.(png|jpg|jpeg|webp|gif)$/i.test(base)) {
      return base;
    }
  } catch {}
  const extension = inferExtensionFromMime(mime);
  return `reference.${extension}`;
}

function createBlobFromReference(value: string): { blob: Blob; filename: string } {
  const trimmed = value.trim();
  const dataUriMatch = trimmed.match(/^data:([^;]+);base64,(.+)$/);
  if (dataUriMatch) {
    const mimeType = dataUriMatch[1] || "image/png";
    const base64 = dataUriMatch[2];
    const bytes = decodeBase64ToUint8Array(base64);
    const blob = new Blob([bytes], { type: mimeType });
    const extension = inferExtensionFromMime(mimeType);
    return { blob, filename: `reference.${extension}` };
  }

  const normalized = trimmed.replace(/\s+/g, "");
  if (/^[A-Za-z0-9+/]+={0,2}$/.test(normalized) && normalized.length > 100) {
    const bytes = decodeBase64ToUint8Array(normalized);
    const blob = new Blob([bytes], { type: "image/png" });
    return { blob, filename: 'reference.png' };
  }

  throw new Error("Reference image must be provided as a base64 data URI or raw base64 string");
}

function decodeBase64ToUint8Array(value: string): Uint8Array {
  const sanitized = value.replace(/\s+/g, "");
  if (typeof atob === "function") {
    const binary = atob(sanitized);
    const length = binary.length;
    const bytes = new Uint8Array(length);
    for (let i = 0; i < length; i += 1) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes;
  }
  const bufferCtor = (globalThis as any)?.Buffer;
  if (typeof bufferCtor === "function") {
    const buf = bufferCtor.from(sanitized, "base64");
    return buf instanceof Uint8Array ? buf : new Uint8Array(buf);
  }
  throw new Error("Base64 decoding is not supported in this environment");
}

function inferExtensionFromMime(mimeType: string): string {
  const lower = mimeType.toLowerCase();
  if (lower.includes("jpeg") || lower.includes("jpg")) return "jpg";
  if (lower.includes("webp")) return "webp";
  if (lower.includes("gif")) return "gif";
  return "png";
}

async function blobToDataURL(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(typeof reader.result === "string" ? reader.result : "");
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}
